import React, {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  Dispatch,
  useEffect,
} from 'react';

import { usePathname } from 'next/navigation';

import { getDomain } from '@utils/getDomain';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import { INITIAL_VALUES, ISearchBar } from './state';
import {
  FlightSearchBarActionType,
  FlightSearchBarActions,
} from './types/flight';
import { commonReducer } from './reducers';
import {
  useCarRentalQuery,
  useFlightQuery,
  useHotelQuery,
  useTourQuery,
} from './hooks';
import { HotelSearchBarActions, HotelSearchBarActionType } from './types/hotel';
import { TourSearchBarActionType, TourSearchBarActions } from './types/tour';
import { CarRentalSearchBarActionType, CarSearchBarActions } from './types/car';

type SearchBarDispatch =
  | FlightSearchBarActions
  | HotelSearchBarActions
  | TourSearchBarActions
  | CarSearchBarActions;

interface ISearchBarProvider {
  children?: ReactNode;
}

const SearchBarContext = createContext<{
  state: ISearchBar;
  dispatch: Dispatch<SearchBarDispatch>;
}>({
  state: INITIAL_VALUES,
  dispatch: () => null,
});

export const SearchBarProvider = ({ children }: ISearchBarProvider) => {
  const [state, dispatch] = useReducer(commonReducer, INITIAL_VALUES);
  const pathname = usePathname();
  const query = useSearchParamsToQuery();

  const isQueryEmpty = !Object.keys(query).length;

  const { isDomainFlight, isDomainHotel, isDomainTour, isDomainCar } =
    getDomain(pathname);

  const flightQueryParams = useFlightQuery(query, isDomainFlight);
  const hotelQueryParams = useHotelQuery(query, isDomainHotel);
  const tourQueryParams = useTourQuery(isDomainTour);
  const carQueryParams = useCarRentalQuery(query, isDomainCar);

  useEffect(() => {
    if (isDomainFlight && flightQueryParams && !isQueryEmpty) {
      dispatch({
        type: FlightSearchBarActionType.SET_ALL,
        payload: flightQueryParams,
      });
    }
  }, [flightQueryParams]);

  useEffect(() => {
    if (isDomainHotel && hotelQueryParams) {
      dispatch({
        type: HotelSearchBarActionType.SET_ALL,
        payload: hotelQueryParams,
      });
    }
  }, [hotelQueryParams]);

  useEffect(() => {
    if (isDomainTour && tourQueryParams) {
      dispatch({
        type: TourSearchBarActionType.SET_ALL,
        payload: tourQueryParams,
      });
    }
  }, [tourQueryParams]);

  useEffect(() => {
    if (isDomainCar && carQueryParams && !isQueryEmpty) {
      dispatch({
        type: CarRentalSearchBarActionType.SET_ALL,
        payload: carQueryParams,
      });
    }
  }, [carQueryParams]);

  return (
    <SearchBarContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchBarContext.Provider>
  );
};

const useSearchBar = () => {
  const context = useContext(SearchBarContext);

  if (context === undefined) {
    throw new Error('useSearchBar must be used within SearchBarContext');
  }

  return context;
};

export default useSearchBar;
